import { lazy, Suspense, useEffect } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'

import { ErrorBoundary } from '@/components/ErrorBoundary'
import { Skeleton } from '@/components/ui/surface'
import { HomePage } from '@/pages/Home'
import { LoginPage } from '@/pages/Login'
import { useAuth } from '@/providers/auth'

// Route-level code splitting, per the frontend render budget in architecture.md §13.
// Login and Home stay in the entry chunk: they are the first paint for every user.
const NotificationsPage = lazy(() =>
  import('@/pages/Notifications').then((m) => ({ default: m.NotificationsPage })),
)
const AppWorkspacePage = lazy(() =>
  import('@/pages/AppWorkspace').then((m) => ({ default: m.AppWorkspacePage })),
)
const TablePage = lazy(() => import('@/pages/TablePage').then((m) => ({ default: m.TablePage })))
const WorkflowsPage = lazy(() =>
  import('@/pages/WorkflowsPage').then((m) => ({ default: m.WorkflowsPage })),
)
const DashboardPage = lazy(() =>
  import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
)
const RecordPage = lazy(() => import('@/pages/RecordPage').then((m) => ({ default: m.RecordPage })))
const SchemaPage = lazy(() =>
  import('@/pages/admin/Schema').then((m) => ({ default: m.SchemaPage })),
)
const UsersPage = lazy(() => import('@/pages/admin/Users').then((m) => ({ default: m.UsersPage })))
const DocumentPage = lazy(() =>
  import('@/pages/DocumentPage').then((m) => ({ default: m.DocumentPage })),
)
const OrganisationPage = lazy(() =>
  import('@/pages/admin/Organisation').then((m) => ({ default: m.OrganisationPage })),
)
const AssistantUsagePage = lazy(() =>
  import('@/pages/admin/AssistantUsage').then((m) => ({ default: m.AssistantUsagePage })),
)
const SystemPage = lazy(() =>
  import('@/pages/admin/System').then((m) => ({ default: m.SystemPage })),
)
const AppsPage = lazy(() => import('@/pages/admin/Apps').then((m) => ({ default: m.AppsPage })))
const IntegrationsPage = lazy(() =>
  import('@/pages/admin/Integrations').then((m) => ({ default: m.IntegrationsPage })),
)
const AccessPage = lazy(() => import('@/pages/admin/Access').then((m) => ({ default: m.AccessPage })))
const AutomationPage = lazy(() =>
  import('@/pages/admin/Automation').then((m) => ({ default: m.AutomationPage })),
)
const AuditPage = lazy(() => import('@/pages/admin/Logs').then((m) => ({ default: m.AuditPage })))
const LoginLogsPage = lazy(() =>
  import('@/pages/admin/Logs').then((m) => ({ default: m.LoginLogsPage })),
)
const ConnectPage = lazy(() => import('@/pages/Connect').then((m) => ({ default: m.ConnectPage })))
const ConnectionsPage = lazy(() =>
  import('@/pages/Connections').then((m) => ({ default: m.ConnectionsPage })),
)
const ApprovePage = lazy(() => import('@/pages/Approve').then((m) => ({ default: m.ApprovePage })))
const ApproveOnePage = lazy(() =>
  import('@/pages/Approve').then((m) => ({ default: m.ApproveOnePage })),
)
const UploadPage = lazy(() => import('@/pages/Upload').then((m) => ({ default: m.UploadPage })))

/** Where a signed-out visitor was going, kept across the sign-in (Google returns to
 *  Home): a link from Claude, ChatGPT or a notification lands where it pointed. */
const RETURN_KEY = 'sbt:return-to'

function rememberReturn(to: string) {
  try {
    sessionStorage.setItem(RETURN_KEY, to)
  } catch {
    // Storage blocked: the visitor lands on Home after signing in.
  }
}

function peekReturn(): string | null {
  try {
    return sessionStorage.getItem(RETURN_KEY)
  } catch {
    return null
  }
}

function takeReturn(): string | null {
  const to = peekReturn()
  try {
    sessionStorage.removeItem(RETURN_KEY)
  } catch {
    // Nothing kept, nothing to clear.
  }
  return to
}

/** Holds the page's shape while /api/auth/me resolves, so nothing jumps. */
function Booting() {
  return (
    <div className="flex h-full flex-col">
      <div
        className="flex items-center gap-3 border-b border-border bg-surface px-4"
        style={{ height: 'var(--topbar-h)' }}
      >
        <Skeleton className="size-6 rounded-md" />
        <Skeleton className="h-3 w-24" />
      </div>
      <div className="mx-auto w-full max-w-5xl px-6 py-10">
        <Skeleton className="h-6 w-56" />
        <div className="mt-6 grid grid-cols-[repeat(auto-fill,minmax(10rem,1fr))] gap-3">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-[7.25rem] rounded-lg" />
          ))}
        </div>
      </div>
    </div>
  )
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { me, loading } = useAuth()
  const location = useLocation()

  if (loading) return <Booting />
  if (!me) {
    if (location.pathname !== '/') rememberReturn(location.pathname + location.search)
    return <Navigate to="/login" replace state={{ from: location.pathname }} />
  }
  // Suspense sits inside the auth gate so a lazy chunk never flashes for a
  // signed-out visitor. The boundary resets on navigation, so a crashed screen
  // does not poison the next one.
  return (
    <ErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={<Booting />}>{children}</Suspense>
    </ErrorBoundary>
  )
}

export function App() {
  const { me, loading } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (!me) return
    const to = takeReturn()
    if (to) navigate(to, { replace: true })
  }, [me, navigate])

  return (
    <Routes>
      <Route
        path="/login"
        element={loading ? <Booting /> : me ? <Navigate to={peekReturn() ?? '/'} replace /> : <LoginPage />}
      />

      <Route
        path="/"
        element={
          <RequireAuth>
            <HomePage />
          </RequireAuth>
        }
      />
      <Route
        path="/notifications"
        element={
          <RequireAuth>
            <NotificationsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/app/:app"
        element={
          <RequireAuth>
            <AppWorkspacePage />
          </RequireAuth>
        }
      />
      <Route
        path="/app/:app/-/workflows"
        element={
          <RequireAuth>
            <WorkflowsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/app/:app/-/:page"
        element={
          <RequireAuth>
            <DashboardPage />
          </RequireAuth>
        }
      />
      <Route
        path="/app/:app/:table"
        element={
          <RequireAuth>
            <TablePage />
          </RequireAuth>
        }
      />
      <Route
        path="/app/:app/:table/:id/print/:document"
        element={
          <RequireAuth>
            <DocumentPage />
          </RequireAuth>
        }
      />
      <Route
        path="/app/:app/:table/:id"
        element={
          <RequireAuth>
            <RecordPage />
          </RequireAuth>
        }
      />

      <Route
        path="/connect"
        element={
          <RequireAuth>
            <ConnectPage />
          </RequireAuth>
        }
      />
      <Route
        path="/connections"
        element={
          <RequireAuth>
            <ConnectionsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/approve"
        element={
          <RequireAuth>
            <ApprovePage />
          </RequireAuth>
        }
      />
      <Route
        path="/approve/:kind/:id"
        element={
          <RequireAuth>
            <ApproveOnePage />
          </RequireAuth>
        }
      />
      <Route
        path="/upload/:token"
        element={
          <RequireAuth>
            <UploadPage />
          </RequireAuth>
        }
      />

      <Route path="/admin" element={<Navigate to="/admin/users" replace />} />
      <Route
        path="/admin/users"
        element={
          <RequireAuth>
            <UsersPage />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/organisation"
        element={
          <RequireAuth>
            <OrganisationPage />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/system"
        element={
          <RequireAuth>
            <SystemPage />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/assistant-usage"
        element={
          <RequireAuth>
            <AssistantUsagePage />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/apps"
        element={
          <RequireAuth>
            <AppsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/integrations"
        element={
          <RequireAuth>
            <IntegrationsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/access"
        element={
          <RequireAuth>
            <AccessPage />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/schema"
        element={
          <RequireAuth>
            <SchemaPage />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/automation"
        element={
          <RequireAuth>
            <AutomationPage />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/audit"
        element={
          <RequireAuth>
            <AuditPage />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/login-logs"
        element={
          <RequireAuth>
            <LoginLogsPage />
          </RequireAuth>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
